<script setup lang="ts">

import { onBeforeMount, onMounted, ref } from 'vue'
import { usePlayer } from '@/composables/game/player'
import { useAnalytics } from '@/composables/analytics'
import api from '@/services/api'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { type OnboardingStep } from '@/types/onboarding'
import OnboardingSteps from '@/components/OnboardingSteps.vue'
import router from '@/router'
import { RouteName } from '@/routes'
import { useGuide } from '@/composables/game/guide'
import { useBackPack } from '@/composables/game/backpack'
import { showOnboardedFromS1Modal, useOnboardingLayout } from '@/composables/game/onboarding'
import { useDropeeSeason1 } from '@/composables/game/dropee-season1'

const { updatePlayer } = usePlayer()
const { addHapticFeedback } = useHapticFeedback()
const { initGuide } = useGuide()
useBackPack({ init: true })
const { steps } = useOnboardingLayout()
const { getSeason1Stats, season1Stats } = useDropeeSeason1()

const currentStep = ref<OnboardingStep>(steps[0])
const isLoading = ref<boolean>(false)
const { triggerEvent, setUserProperty } = useAnalytics()

onBeforeMount(() => {
  getSeason1Stats()
})

function goNext() {
  if (currentStep.value.index !== steps.length - 1) {
    currentStep.value = steps[currentStep.value.index + 1]
  } else {
    onFinish()
  }
}

async function onFinish() {
  addHapticFeedback('medium')
  isLoading.value = true
  try {
    const { playerStats, isReferred } = await api.onboardingDone()
    updatePlayer(playerStats)
    triggerEvent({ event: 'tutorial_complete' })
    triggerEvent({ event: isReferred ? 'referral' : 'non_referral' })
    setUserProperty({ acquisition: isReferred ? 'referral' : 'direct' })
    if (season1Stats.value) {
      showOnboardedFromS1Modal.value = true
      await router.push({ name: RouteName.REWARDS })
    } else {
      await router.push({ name: RouteName.REWARDS })
    }
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  triggerEvent({ event: 'tutorial_begin' })
  initGuide()
})
</script>
<template>
  <div
    class="flex-1 flex flex-col"
  >
    <OnboardingSteps
      :current-step="currentStep"
      :steps="steps"
      :is-loading="isLoading"
      @set-step="currentStep = steps[$event]"
      @go-next="goNext"
    />
  </div>
</template>
